import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
// @mui material components
import Card from "@mui/material/Card";
import { useMediaQuery } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import Switch from "@mui/material/Switch";
import PaymentsIcon from "@mui/icons-material/Payments";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AppleIcon from "@mui/icons-material/Apple";
import moment from "moment";
import { FcGoogle } from "react-icons/fc";

// Material Dashboard 2 React components
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// API
import {
  GetAllUsersCall,
  GetUserByIdCall,
  UpdateUserStatusCall,
  ResetUserPasswordCall,
  UpdateUserEarningCall,
  UpdateUserProfileVerifiedCall,
} from "../../../../redux/api/auth";
import { useSelector } from "react-redux";
import AddTransactionDrawer from "components/AddTransactionDrawer";
import CountrySelect from "./Countries";

const MICRO_LAB_MARK = require("../../../../assets/images/logos/mini_logo.png");

/** Apple Hide My Email — infer Apple signup when DB `social_auth_provider` was not stored. */
function resolveSignupProvider(socialAuthProvider, email) {
  const p = socialAuthProvider ? String(socialAuthProvider).toLowerCase().trim() : "";
  if (p === "apple" || p === "google") return p;
  const e = String(email || "").toLowerCase();
  if (e.includes("privaterelay.appleid.com")) return "apple";
  return p;
}

// Country flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return null;
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

function Projects({ search = "" }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isAdmin = useSelector((state) => state.auth?.role === "admin");
  const headerBg =
    theme.palette.background?.card ??
    theme.palette.background?.paper ??
    theme.palette.background?.default;
  const headerTextColor = theme.palette.text.primary;
  const cardBg =
    theme.palette.background?.card ??
    theme.palette.background?.paper ??
    theme.palette.background?.default;

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [user_id, set_user_id] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);

  // 3-dots menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  // User info modal
  const [userInfoOpen, setUserInfoOpen] = useState(false);
  const [userInfoUserId, setUserInfoUserId] = useState(null);
  const [userInfoData, setUserInfoData] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);

  // Reset password confirmation
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  // Filter drawer
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({
    university: "",
    occupation: "",
    country: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    university: "",
    occupation: "",
    country: "",
  });

  // Search by name or email (case-insensitive on backend)
  const [searchInput, setSearchInput] = useState(() => (typeof search === "string" ? search : ""));
  const searchTerm = (typeof searchInput === "string" ? searchInput : "").trim();

  const cellSx = {
    py: { xs: 1, sm: 1.5 },
    px: { xs: 1, sm: 1.5 },
    fontSize: { xs: "0.7rem", sm: "0.8rem" },
    whiteSpace: "nowrap",
  };

  const fetchUsers = async (pageIndex = 0, pageSize = 25) => {
    const response = await GetAllUsersCall({
      page: pageIndex + 1,
      limit: pageSize,
      search: searchTerm || search,
      university: appliedFilters.university,
      occupation: appliedFilters.occupation,
      country: appliedFilters.country,
    });
    if (response?.rows) {
      setRows(response.rows);
      setTotal(response.total || 0);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers(page, limit);
  }, [page, limit, appliedFilters, searchTerm, search]);

  const openMenu = (e, user) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuUser(user);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  };

  useEffect(() => {
    if (!userInfoOpen || !userInfoUserId) return;
    let mounted = true;
    setUserInfoLoading(true);
    GetUserByIdCall(userInfoUserId)
      .then((res) => {
        if (!mounted) return;
        const payload = res?.data ?? res;
        if (payload && typeof payload === "object") {
          setUserInfoData(payload);
        }
      })
      .catch(() => {
        if (mounted) setUserInfoLoading(false);
      })
      .finally(() => {
        if (mounted) setUserInfoLoading(false);
      });
    return () => { mounted = false; };
  }, [userInfoOpen, userInfoUserId]);

  const handleUserInfo = () => {
    if (menuUser) {
      setUserInfoUserId(menuUser.id);
      setUserInfoData(menuUser);
      setUserInfoOpen(true);
    }
    closeMenu();
  };
  const handleAddSubscription = () => {
    if (menuUser) {
      set_user_id(menuUser.id);
      setOpenDrawer(true);
    }
    closeMenu();
  };
  const handleToggleStatus = async () => {
    if (!menuUser) return;
    const newStatus = menuUser.status === "active" ? "inactive" : "active";
    const res = await UpdateUserStatusCall(menuUser.id, newStatus);
    if (res?.status === 200) fetchUsers(page, limit);
    closeMenu();
  };
  const handleResetPasswordClick = () => {
    if (menuUser) setResetPasswordUser(menuUser);
    closeMenu();
    setResetPasswordConfirmOpen(true);
  };
  const handleEarningToggle = async (e) => {
    e.stopPropagation();
    if (!menuUser) return;
    const next = !menuUser.enabled_earning;
    const res = await UpdateUserEarningCall(menuUser.id, next);
    if (res?.status === 200) {
      setMenuUser((u) => (u ? { ...u, enabled_earning: next } : u));
      setRows((prev) =>
        prev.map((r) => (r.id === menuUser.id ? { ...r, enabled_earning: next } : r))
      );
    }
  };

  const handleProfileVerifiedToggle = async (e) => {
    e.stopPropagation();
    if (!menuUser) return;
    const next = !menuUser.profile_verified;
    const res = await UpdateUserProfileVerifiedCall(menuUser.id, next);
    if (res?.status === 200) {
      setMenuUser((u) => (u ? { ...u, profile_verified: next } : u));
      setRows((prev) =>
        prev.map((r) => (r.id === menuUser.id ? { ...r, profile_verified: next } : r))
      );
    }
  };
  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordUser) return;
    await ResetUserPasswordCall(resetPasswordUser.id);
    setResetPasswordConfirmOpen(false);
    setResetPasswordUser(null);
  };

  const Author = ({ image, name, email, countryCode, socialAuthProvider }) => {
    const flag = getCountryFlag(countryCode);
    const provider = resolveSignupProvider(socialAuthProvider, email);

    return (
      <MDBox
        display="flex"
        alignItems="center"
        lineHeight={1}
        gap={1}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", flexShrink: 0, width: 36, height: 36 }}>
          <MDAvatar
            src={image}
            alt={name}
            size="sm"
            sx={{ width: 36, height: 36, flexShrink: 0 }}
          />
          <Box
            component="span"
            title={
              provider === "apple"
                ? "Apple Sign In"
                : provider === "google"
                  ? "Google Sign In"
                  : "Microlab (email / password)"
            }
            sx={{
              position: "absolute",
              right: -2,
              bottom: -2,
              width: 16,
              height: 16,
              borderRadius: "5px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.paper",
              boxShadow: (theme) => theme.shadows[2],
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {provider === "apple" ? (
              <AppleIcon sx={{ fontSize: "0.7rem", color: "#000" }} />
            ) : provider === "google" ? (
              <FcGoogle style={{ width: 11, height: 11 }} />
            ) : (
              <Box
                component="img"
                src={MICRO_LAB_MARK}
                alt=""
                sx={{ width: 11, height: 11, objectFit: "contain", display: "block" }}
              />
            )}
          </Box>
        </Box>
        {flag && (
          <Box
            sx={{
              fontSize: "1.2rem",
              lineHeight: 1,
              width: "20px",
              minWidth: "20px",
              maxWidth: "20px",
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {flag}
          </Box>
        )}
        <MDBox
          lineHeight={1}
          sx={{
            minWidth: 0,
            overflow: "hidden",
            flex: 1,
          }}
        >
          <MDTypography
            display="block"
            variant="button"
            fontWeight="medium"
            sx={{
              fontSize: "0.75rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {name}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontSize: "0.65rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              display: "block",
            }}
          >
            {email}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: "success",
      inactive: "error",
      pending: "warning",
      suspended: "default",
    };
    return statusColors[status?.toLowerCase()] || "default";
  };

  const applyFilters = () => {
    setAppliedFilters({
      university: filterValues.university,
      occupation: filterValues.occupation,
      country: filterValues.country,
    });
    setFilterOpen(false);
    setPage(0);
  };

  const clearFilters = () => {
    setFilterValues({
      university: "",
      occupation: "",
      country: "",
    });
    setAppliedFilters({
      university: "",
      occupation: "",
      country: "",
    });
    setFilterOpen(false);
    setPage(0);
  };

  const activeFilterCount =
    Object.values(appliedFilters).filter(Boolean).length;

  return (
    <Card sx={{ boxShadow: 3 }}>
      {/* Header */}
      <MDBox p={3}>
        {/* Row: left = title + total (column), right = search (desktop) + Filter */}
        <MDBox
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            <MDTypography variant="h5" fontWeight="bold">
              Microlab Users
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon
                sx={{
                  fontSize: 18,
                  color: ({ palette: { primary } }) => primary.main,
                }}
              >
                people
              </Icon>
              <MDTypography variant="button" fontWeight="regular" color="text">
                <strong>{total}</strong> users total
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" gap={2} alignItems="center" flexShrink={0}>
            <TextField
              size="small"
              placeholder="Search by name or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                ),
              }}
              sx={{
                display: { xs: "none", md: "flex" },
                minWidth: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                },
              }}
            />
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(true)}
                color="primary"
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  color: "#ffff",
                }}
              >
                Filters
              </Button>
            </Badge>
          </MDBox>
        </MDBox>

        {/* Mobile: search input full width under the row */}
        <MDBox sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "action.active" }} />
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
              },
            }}
          />
        </MDBox>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <MDBox
            mt={2}
            display="flex"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <MDTypography variant="caption" fontWeight="medium" color="text">
              Active Filters:
            </MDTypography>
            {appliedFilters.university && (
              <Chip
                label={`University: ${appliedFilters.university}`}
                size="small"
                onDelete={() => {
                  setAppliedFilters({ ...appliedFilters, university: "" });
                  setFilterValues({ ...filterValues, university: "" });
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {appliedFilters.occupation && (
              <Chip
                label={`Occupation: ${appliedFilters.occupation}`}
                size="small"
                onDelete={() => {
                  setAppliedFilters({ ...appliedFilters, occupation: "" });
                  setFilterValues({ ...filterValues, occupation: "" });
                }}
                color="primary"
                variant="outlined"
              />
            )}
            {appliedFilters.country && (
              <Chip
                label={`Country: ${appliedFilters.country}`}
                size="small"
                onDelete={() => {
                  setAppliedFilters({ ...appliedFilters, country: "" });
                  setFilterValues({ ...filterValues, country: "" });
                }}
                color="primary"
                variant="outlined"
              />
            )}
            <Chip
              label="Clear All"
              size="small"
              onClick={clearFilters}
              color="error"
              variant="outlined"
              icon={<ClearIcon />}
            />
          </MDBox>
        )}
      </MDBox>

      <Divider />

      {/* Desktop: Table */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer
          sx={{
            width: "100%",
            height: "600px",
            overflow: "auto",
          }}
        >
          <Table
          size="small"
          stickyHeader
          padding="normal"
          sx={{
            tableLayout: "fixed", // ✅ Fixed table layout for consistent columns
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: headerBg }}>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "200px",
                  fontWeight: "bold",
                  pr: -20,
                  backgroundColor: "inherit",
                  color: headerTextColor,
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "60px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
                align="left"
              >
                Platform
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "60px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
                align="left"
              >
                Subscription
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "80px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  pr: 10,
                  color: headerTextColor,
                }}
                align="right"
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "150px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                University
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "100px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                Version
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "80px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                OS
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "120px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                Device
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "80px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                Occupation
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "130px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                Created
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "130px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
              >
                Updated
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  width: "80px",
                  backgroundColor: "inherit",
                  fontWeight: "bold",
                  color: headerTextColor,
                }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell
                  sx={{
                    ...cellSx,
                    width: "200px",
                  }}
                >
                  <Author
                    image={user.profile_image || user.avatar}
                    name={`${user.first_name} ${user.last_name}`}
                    email={user.email}
                    countryCode={user.country}
                    socialAuthProvider={user.social_auth_provider}
                  />
                </TableCell>

                {/* Platform */}
                <TableCell sx={{ ...cellSx, width: "60px" }} align="center">
                  {user.platform === "ios" ? (
                    <Icon sx={{ color: "#555" }} fontSize="small">
                      apple
                    </Icon>
                  ) : user.platform === "android" ? (
                    <Icon sx={{ color: "success.main" }} fontSize="small">
                      android
                    </Icon>
                  ) : (
                    "—"
                  )}
                </TableCell>

                {/* Subscription */}
                <TableCell sx={{ ...cellSx, width: "60px" }} align="center">
                  {user?.current_subscription === "subscribe" ? (
                    <>💰</>
                  ) : (
                    <>🕛</>
                  )}
                </TableCell>

                <TableCell sx={{ ...cellSx, width: "80px" }} align="center">
                  <Chip
                    label={user.status || "N/A"}
                    size="small"
                    color={getStatusColor(user.status)}
                  />
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "150px",
                    // display: { xs: "none", md: "table-cell" },
                  }}
                >
                  {user.university
                    ? user.university.length > 15
                      ? user.university.substring(0, 15) + "..."
                      : user.university
                    : "—"}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "100px",
                    // display: { xs: "none", md: "table-cell" },
                  }}
                >
                  {user.version || "—"}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "80px",
                  }}
                >
                  {user.os_version || "—"}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "120px",
                    maxWidth: "120px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {user.device_model || "—"}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "80px",
                    // display: { xs: "none", md: "table-cell" },
                  }}
                >
                  {user.occupation || "—"}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "130px",
                    // display: { xs: "none", md: "table-cell" },
                  }}
                >
                  {moment(user.created_at).format("MMM DD, YY HH:mm")}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx,
                    width: "130px",
                    // display: { xs: "none", md: "table-cell" },
                  }}
                >
                  {moment(user.updated_at).format("MMM DD, YY HH:mm")}
                </TableCell>

                <TableCell sx={{ ...cellSx, width: "80px" }} align="center">
                  <IconButton size="small" onClick={(e) => openMenu(e, user)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Mobile: Card list */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          overflowY: "auto",
          minHeight: 400,
          pb: 2,
        }}
      >
        {rows.map((user) => (
          <Card
            key={user.id}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: (t) => (t.palette.mode === "dark" ? 0 : 1),
              border: "1px solid",
              borderColor: "divider",
              bgcolor: cardBg,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={2}>
                <Author
                  image={user.profile_image || user.avatar}
                  name={`${user.first_name} ${user.last_name}`}
                  email={user.email}
                  countryCode={user.country}
                  socialAuthProvider={user.social_auth_provider}
                />
                <IconButton size="small" onClick={(e) => openMenu(e, user)} sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </MDBox>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  fontSize: "0.8rem",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Platform
                  </MDTypography>
                  <MDBox component="span" sx={{ display: "inline-flex", alignItems: "center", fontSize: "1.6rem" }}>
                    {user.platform === "ios" ? (
                      <Icon sx={{ color: "#555", fontSize: "inherit" }}>apple</Icon>
                    ) : user.platform === "android" ? (
                      <Icon sx={{ color: "success.main", fontSize: "inherit" }}>android</Icon>
                    ) : (
                      "—"
                    )}
                  </MDBox>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Subscription
                  </MDTypography>
                  <span>{user?.current_subscription === "subscribe" ? "💰" : "🕛"}</span>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Status
                  </MDTypography>
                  <Chip label={user.status || "N/A"} size="small" color={getStatusColor(user.status)} />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    University
                  </MDTypography>
                  <MDTypography variant="caption" noWrap>
                    {user.university || "—"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Version
                  </MDTypography>
                  <span>{user.version || "—"}</span>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    OS
                  </MDTypography>
                  <MDTypography variant="caption" noWrap>
                    {user.os_version || "—"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="flex-start" gap={0.5} sx={{ gridColumn: "1 / -1" }}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium" sx={{ flexShrink: 0 }}>
                    Device
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ wordBreak: "break-word" }}>
                    {user.device_model || "—"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Occupation
                  </MDTypography>
                  <span>{user.occupation || "—"}</span>
                </MDBox>
                <MDBox sx={{ gridColumn: "1 / -1" }} display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Created
                  </MDTypography>
                  <span>{moment(user.created_at).format("MMM DD, YY HH:mm")}</span>
                </MDBox>
                <MDBox sx={{ gridColumn: "1 / -1" }} display="flex" alignItems="center" gap={0.5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                    Updated
                  </MDTypography>
                  <span>{moment(user.updated_at).format("MMM DD, YY HH:mm")}</span>
                </MDBox>
              </MDBox>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={limit}
        onRowsPerPageChange={(event) => {
          setLimit(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
        sx={{
          borderTop: 1,
          borderColor: "divider",
        }}
      />

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            boxShadow: 5,
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Drawer Header */}
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5" fontWeight="bold" color="white">
                Filter Users
              </MDTypography>
              <IconButton
                onClick={() => setFilterOpen(false)}
                sx={{ color: "white" }}
              >
                <ClearIcon />
              </IconButton>
            </MDBox>
            <MDTypography
              variant="caption"
              color="white"
              sx={{ opacity: 0.9, mt: 1 }}
            >
              Refine your search with filters below
            </MDTypography>
          </Box>

          {/* Filter Form */}
          <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="University"
                placeholder="e.g., MIT, Stanford, Harvard"
                variant="outlined"
                value={filterValues.university}
                onChange={(e) =>
                  setFilterValues({
                    ...filterValues,
                    university: e.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="occupation-label">Occupation</InputLabel>
                <Select
                  labelId="occupation-label"
                  id="occupation-select"
                  value={filterValues.occupation}
                  label="Occupation"
                  onChange={(e) =>
                    setFilterValues({
                      ...filterValues,
                      occupation: e.target.value,
                    })
                  }
                  sx={{
                    height: 45,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="professor">Professor</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <CountrySelect
                value={filterValues.country}
                onChange={(e) =>
                  setFilterValues({ ...filterValues, country: e.target.value })
                }
              />
            </Box>
          </Box>

          {/* Drawer Footer */}
          <Box
            sx={{
              p: 3,
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: ({ palette }) => palette.background.default,
            }}
          >
            <Box display="flex" gap={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  color: "#ffff",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                  },
                }}
              >
                Clear All
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={applyFilters}
                startIcon={<FilterListIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.5,
                  color: "#ffff",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                  },
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* 3-dots Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <MenuItem onClick={handleUserInfo}>
          <PersonIcon sx={{ mr: 1.5, fontSize: 20 }} /> User info
        </MenuItem>
        <MenuItem onClick={handleAddSubscription}>
          <CardGiftcardIcon sx={{ mr: 1.5, fontSize: 20 }} /> Add subscription
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {menuUser?.status === "active" ? (
            <><ToggleOffIcon sx={{ mr: 1.5, fontSize: 20 }} /> Deactivate user</>
          ) : (
            <><ToggleOnIcon sx={{ mr: 1.5, fontSize: 20 }} /> Activate user</>
          )}
        </MenuItem>
        <MenuItem onClick={handleResetPasswordClick}>
          <LockResetIcon sx={{ mr: 1.5, fontSize: 20 }} /> Reset password
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={(e) => e.stopPropagation()}>
            <PaymentsIcon sx={{ mr: 1.5, fontSize: 20 }} />
            <MDBox component="span" sx={{ flex: 1 }}>Earning tab</MDBox>
            <Switch
              size="small"
              checked={Boolean(menuUser?.enabled_earning)}
              onChange={handleEarningToggle}
              color="primary"
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={(e) => e.stopPropagation()}>
            <VerifiedUserIcon sx={{ mr: 1.5, fontSize: 20, color: "info.main" }} />
            <MDBox component="span" sx={{ flex: 1 }}>Verified badge</MDBox>
            <Switch
              size="small"
              checked={Boolean(menuUser?.profile_verified)}
              onChange={handleProfileVerifiedToggle}
              color="info"
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
      </Menu>

      {/* User info modal */}
      <Dialog
        open={userInfoOpen}
        onClose={() => { setUserInfoOpen(false); setUserInfoUserId(null); setUserInfoData(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: cardBg } }}
      >
        <DialogTitle sx={{ color: headerTextColor }}>
          User info
        </DialogTitle>
        <DialogContent>
          {userInfoLoading && (
            <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>Loading details…</MDTypography>
          )}
          {userInfoData && (
            <MDBox sx={{ color: "text.primary" }}>
              <MDBox display="flex" alignItems="center" gap={2} mb={2} pb={2} borderBottom="1px solid" borderColor="divider">
                <MDAvatar
                  src={userInfoData.profile_image || userInfoData.avatar}
                  alt={`${userInfoData.first_name} ${userInfoData.last_name}`}
                  size="lg"
                  sx={{ width: 56, height: 56 }}
                />
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDTypography fontWeight="bold" variant="h6" color="text.primary">
                      {userInfoData.first_name} {userInfoData.last_name}
                    </MDTypography>
                    {userInfoData.profile_verified ? (
                      <Chip
                        size="small"
                        icon={<VerifiedUserIcon sx={{ fontSize: 16 }} />}
                        label="Verified"
                        color="info"
                        variant="outlined"
                      />
                    ) : null}
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">{userInfoData.email}</MDTypography>
                  <Chip size="small" label={userInfoData.status} color={getStatusColor(userInfoData.status)} sx={{ mt: 0.5 }} />
                </MDBox>
              </MDBox>
              <MDTypography variant="caption" fontWeight="600" display="block" sx={{ mt: 2, mb: 0.5, color: "text.primary" }}>Profile</MDTypography>
              <List dense disablePadding sx={{ bgcolor: "grey.100", borderRadius: 1, py: 0, color: "text.primary" }}>
                <ListItem><ListItemText primary="University" secondary={userInfoData.university || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Occupation" secondary={userInfoData.occupation || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Country" secondary={userInfoData.country || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Platform" secondary={userInfoData.platform || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="App version" secondary={userInfoData.version || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="OS version" secondary={userInfoData.os_version || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Device" secondary={userInfoData.device_model || "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Created" secondary={userInfoData.created_at ? moment(userInfoData.created_at).format("MMM DD, YYYY HH:mm") : "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
              </List>
              <MDTypography variant="caption" fontWeight="600" display="block" sx={{ mt: 2, mb: 0.5, color: "text.primary" }}>Activity & stats</MDTypography>
              <List dense disablePadding sx={{ bgcolor: "grey.100", borderRadius: 1, py: 0, color: "text.primary" }}>
                <ListItem><ListItemText primary="Quiz attempts" secondary={userInfoData.stats?.quiz_attempts ?? "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Total quiz score" secondary={userInfoData.stats?.total_quiz_score ?? "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Questions" secondary={userInfoData.stats?.questions_count ?? "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Comments" secondary={userInfoData.stats?.comments_count ?? "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
                <ListItem><ListItemText primary="Transactions" secondary={userInfoData.stats?.transactions_count ?? "—"} primaryTypographyProps={{ fontSize: "0.85rem", color: "text.primary" }} secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }} /></ListItem>
              </List>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
          <MDButton variant="outlined" onClick={() => { setUserInfoOpen(false); setUserInfoUserId(null); setUserInfoData(null); }}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* Reset password confirmation */}
      <Dialog
        open={resetPasswordConfirmOpen}
        onClose={() => { setResetPasswordConfirmOpen(false); setResetPasswordUser(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: cardBg } }}
      >
        <DialogTitle sx={{ color: headerTextColor }}>Reset password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            Set this user&apos;s password to <strong>12341234</strong> for urgent login? They should change it after first login.
            {resetPasswordUser && (
              <span> User: {resetPasswordUser.first_name} {resetPasswordUser.last_name} ({resetPasswordUser.email})</span>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
          <MDButton onClick={() => { setResetPasswordConfirmOpen(false); setResetPasswordUser(null); }}>Cancel</MDButton>
          <MDButton variant="contained" color="primary" onClick={handleResetPasswordConfirm}>
            Reset to 12341234
          </MDButton>
        </DialogActions>
      </Dialog>

      <AddTransactionDrawer
        open={openDrawer}
        user_id={user_id}
        toggleDrawer={setOpenDrawer}
        onClose={() => setOpenDrawer(false)}
      />
    </Card>
  );
}

export default Projects;
